import { useFrame, useThree } from '@react-three/fiber';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { type BatchedMesh, Color, Matrix4, MeshBasicMaterial, Object3D } from 'three';

import { LayerType } from 'app/modules/wallforming/components/scenes/camera-controls/types/layerType';
import { usePoiBatchedMeshHandler } from 'app/modules/wallforming/components/scenes/objects/pois/hooks/usePoiBatchedMeshHandler';
import { PoiGeometryType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiMeshType';
import { PoiPointEntry } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiPointEntry';
import { raycastPoiPoints } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiRaycastUtils';
import {
    applyPoiPointTransform,
    createPoiGeometryByType,
    updatePoiBatchedMatrices
} from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';

import type { PoiBatchedEntry } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiBatchedEntry';

const selectionColor = new Color('#649fda');
const dummyMesh = new Object3D();
const matrixScratch = new Matrix4();
const pointsGeometryByType = createPoiGeometryByType(PoiGeometryType.POINTS);

interface PoiPointsProps {
    entries: PoiPointEntry[];
    showVisuals?: boolean;
}

export const PoiPoints = ({ entries, showVisuals = true }: PoiPointsProps) => {
    const poiMaterial = useMemo(
        () =>
            new MeshBasicMaterial({
                depthTest: false,
                transparent: true
            }),
        []
    );
    useEffect(() => () => poiMaterial.dispose(), [poiMaterial]);
    const camera = useThree((state) => state.camera);
    const size = useThree((state) => state.size);
    const previousCameraWorld = useRef<Matrix4>(new Matrix4());
    const previousProjection = useRef<Matrix4>(new Matrix4());

    const batchedEntries = useMemo<PoiBatchedEntry[]>(
        () =>
            entries.map((entry) => ({
                poiIndex: entry.poiIndex,
                matrix: new Matrix4(),
                defaultColor: entry.defaultColor,
                hoverColor: entry.hoverColor,
                selectedColor: selectionColor,
                filterType: entry.filterType
            })),
        [entries]
    );

    const {
        meshRef,
        handleMeshRef: handleBatchedMeshRef,
        handleOnPoiSelection,
        handleOnPointerMove,
        handleOnPointerLeave,
        instanceIdByEntryIndexRef
    } = usePoiBatchedMeshHandler({ entries: batchedEntries, geometriesByType: pointsGeometryByType });

    const applyMatrix = useCallback(
        (entry: PoiPointEntry, matrix: Matrix4) => {
            applyPoiPointTransform(dummyMesh, entry, camera, size.height);
            dummyMesh.updateMatrix();
            matrix.copy(dummyMesh.matrix);
        },
        [camera, size.height]
    );

    const updatePointMatrices = useCallback(() => {
        const mesh = meshRef.current;
        if (mesh) {
            updatePoiBatchedMatrices(mesh, entries, instanceIdByEntryIndexRef.current, applyMatrix, matrixScratch);
            previousCameraWorld.current.copy(camera.matrixWorld);
            previousProjection.current.copy(camera.projectionMatrix);
        }
    }, [applyMatrix, camera, entries, instanceIdByEntryIndexRef, meshRef]);

    useFrame(() => {
        if (
            !previousCameraWorld.current.equals(camera.matrixWorld) ||
            !previousProjection.current.equals(camera.projectionMatrix)
        ) {
            updatePointMatrices();
        }
    });

    useEffect(() => {
        updatePointMatrices();
    }, [entries, size.height, updatePointMatrices]);

    const handlePoiMeshRef = useCallback(
        (mesh: BatchedMesh | null) => {
            handleBatchedMeshRef(mesh);
            if (!mesh) {
                return;
            }
            updatePointMatrices();
        },
        [handleBatchedMeshRef, updatePointMatrices]
    );

    if (entries.length === 0) {
        return null;
    }

    return (
        <batchedMesh
            ref={handlePoiMeshRef}
            args={[40_000, 40_000, 40_000, poiMaterial]}
            layers={[LayerType.RAYCAST]}
            material-visible={showVisuals}
            name="poi-interaction-target"
            perObjectFrustumCulled={true}
            raycast={raycastPoiPoints}
            renderOrder={100}
            onPointerDown={handleOnPoiSelection}
            onPointerLeave={handleOnPointerLeave}
            onPointerMove={handleOnPointerMove}
        />
    );
};
